import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, TextField, Typography } from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Flex } from '../Flex';
import { BBSelect } from '../BBSelect';

/**
 * Custom pagination controls for AG Grid with jump-to-page functionality.
 * Works with both client-side and server-side row models.
 *
 * @param {Object} props
 * @param {Object} props.gridApi - AG Grid API instance
 * @param {number} props.currentPage - Current page (1-indexed for display)
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.totalRows - Total number of rows
 * @param {number} props.pageSize - Current page size
 * @param {number[]} props.pageSizeOptions - Available page size options
 * @param {boolean} props.isLoading - Loading state
 * @param {boolean} props.isServerSide - Whether using server-side pagination
 * @param {Function} props.onPageChange - Callback when page changes (for server-side)
 * @param {Function} props.onPageSizeChange - Callback when page size changes (for server-side)
 */
const AGGridPagination = ({
  gridApi,
  currentPage = 1,
  totalPages = 1,
  totalRows = 0,
  pageSize = 100,
  pageSizeOptions = [50, 100, 500, 1000],
  isLoading = false,
  isServerSide = false,
  onPageChange,
  onPageSizeChange,
}) => {
  const [jumpToPage, setJumpToPage] = useState('');

  // Navigation handlers - use gridApi for client-side, callbacks for server-side
  const goToFirstPage = useCallback(() => {
    if (isServerSide) {
      onPageChange?.(1);
    } else if (gridApi) {
      gridApi.paginationGoToFirstPage();
    }
  }, [gridApi, isServerSide, onPageChange]);

  const goToPreviousPage = useCallback(() => {
    if (isServerSide) {
      onPageChange?.(Math.max(1, currentPage - 1));
    } else if (gridApi) {
      gridApi.paginationGoToPreviousPage();
    }
  }, [gridApi, isServerSide, currentPage, onPageChange]);

  const goToNextPage = useCallback(() => {
    if (isServerSide) {
      onPageChange?.(Math.min(totalPages, currentPage + 1));
    } else if (gridApi) {
      gridApi.paginationGoToNextPage();
    }
  }, [gridApi, isServerSide, currentPage, totalPages, onPageChange]);

  const goToLastPage = useCallback(() => {
    if (isServerSide) {
      onPageChange?.(totalPages);
    } else if (gridApi) {
      gridApi.paginationGoToLastPage();
    }
  }, [gridApi, isServerSide, totalPages, onPageChange]);

  const handleJumpToPage = useCallback(() => {
    const pageNum = parseInt(jumpToPage, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      if (isServerSide) {
        onPageChange?.(pageNum);
      } else if (gridApi) {
        // AG Grid uses 0-indexed pages
        gridApi.paginationGoToPage(pageNum - 1);
      }
      setJumpToPage('');
    }
  }, [gridApi, jumpToPage, totalPages, isServerSide, onPageChange]);

  const handlePageSizeChange = useCallback(
    (event) => {
      const newSize = event.target.value;
      if (onPageSizeChange) {
        onPageSizeChange(newSize);
      } else if (gridApi && typeof gridApi.setGridOption === 'function') {
        gridApi.setGridOption('paginationPageSize', newSize);
      }
    },
    [gridApi, onPageSizeChange]
  );

  const handleJumpKeyPress = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleJumpToPage();
      }
    },
    [handleJumpToPage]
  );

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <Flex.H
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 0',
        backgroundColor: 'transparent',
        gap: '20px',
      }}
    >
      {/* Left side: Jump to page */}
      <Flex.H sx={{ gap: '10px', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          Go to:
        </Typography>

        <TextField
          size="small"
          type="number"
          value={jumpToPage}
          onChange={(e) => setJumpToPage(e.target.value)}
          onKeyPress={handleJumpKeyPress}
          disabled={isLoading}
          placeholder="Page"
          inputProps={{
            min: 1,
            max: totalPages,
            style: { width: '60px', textAlign: 'center' },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '32px',
            },
          }}
        />

        <Button
          variant="contained"
          color="secondary"
          onClick={handleJumpToPage}
          disabled={isLoading || !jumpToPage}
          sx={{ height: '32px', minHeight: '32px', padding: '4px 16px' }}
        >
          GO
        </Button>
      </Flex.H>

      {/* Center: Navigation buttons and page info */}
      <Flex.H sx={{ gap: 1, alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          disabled={isFirstPage || isLoading}
          onClick={goToFirstPage}
          sx={{ minWidth: '36px', height: '32px', minHeight: '32px', padding: '4px 8px' }}
        >
          <FirstPageIcon fontSize="small" />
        </Button>

        <Button
          size="small"
          variant="outlined"
          color="secondary"
          disabled={isFirstPage || isLoading}
          onClick={goToPreviousPage}
          sx={{ minWidth: '36px', height: '32px', minHeight: '32px', padding: '4px 8px' }}
        >
          <NavigateBeforeIcon fontSize="small" />
        </Button>

        <Typography variant="body2" sx={{ mx: 1, whiteSpace: 'nowrap' }}>
          Page {currentPage} of {totalPages}
          {totalRows > 0 && ` (${totalRows.toLocaleString()} rows)`}
        </Typography>

        <Button
          size="small"
          variant="outlined"
          color="secondary"
          disabled={isLastPage || isLoading}
          onClick={goToNextPage}
          sx={{ minWidth: '36px', height: '32px', minHeight: '32px', padding: '4px 8px' }}
        >
          <NavigateNextIcon fontSize="small" />
        </Button>

        <Button
          size="small"
          variant="outlined"
          color="secondary"
          disabled={isLastPage || isLoading}
          onClick={goToLastPage}
          sx={{ minWidth: '36px', height: '32px', minHeight: '32px', padding: '4px 8px' }}
        >
          <LastPageIcon fontSize="small" />
        </Button>
      </Flex.H>

      {/* Right side: Page size selector */}
      <Flex.H sx={{ gap: '10px', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          Page size:
        </Typography>

        <BBSelect
          value={pageSize}
          options={pageSizeOptions.map((size) => ({
            value: size,
            label: size.toLocaleString(),
          }))}
          onChange={(option) => handlePageSizeChange({ target: { value: option.value } })}
          isDisabled={isLoading}
          isSearchable={false}
          isClearable={false}
          menuPlacement="top"
          inputHeight="32px"
          sx={{ minWidth: '120px' }}
        />
      </Flex.H>
    </Flex.H>
  );
};

AGGridPagination.propTypes = {
  gridApi: PropTypes.object,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalRows: PropTypes.number,
  pageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  isLoading: PropTypes.bool,
  isServerSide: PropTypes.bool,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
};

export default AGGridPagination;
